import { RadioGroup, RadioGroupItem } from "@gigpro/ui/RadioGroup";
import { cn } from "@gigpro/ui/tailwind/cn";
import type { ColorSchemeEnum, Uniform } from "@sidegigllc/api/client";
import { COLOR_SCHEME_LABELS } from "api/resources/uniforms";
import React from "react";
import customUniformImage from "./custom.png";

/**
 * The selected value for the preset section: a preset chosen by its `color_scheme` (which implies
 * `selection_type=PRESET`), the literal `"CUSTOM"` to expand the custom builder, or `"NOT_REQUIRED"`
 * for the explicit "no uniform required" choice (CORE-6537, emits `selection_type=NOT_REQUIRED`).
 */
export type UniformSectionValue = ColorSchemeEnum | "CUSTOM" | "NOT_REQUIRED";

interface UniformSectionProps {
  /**
   * The position's uniform presets (Black-on-Black / White-on-Black). `null`/absent when the position
   * default is Custom (`GET /skills/{id}/uniform/` returns `uniform: null`), in which case the section
   * shows only the "Custom uniform" option so the layout stays consistent with the has-presets case.
   */
  uniform?: Uniform | null;
  value: UniformSectionValue | undefined;
  onValueChange: (value: UniformSectionValue) => void;
  className?: string;
}

const cardClass =
  "group cursor-pointer rounded-sm border-2 border-light-gray bg-white p-3 text-left text-normal text-black transition-colors hover:border-brand data-[isselected=true]:border-brand data-[isselected=true]:bg-accent-light-green";

/**
 * W1 — Uniform preset section. Renders a position's uniform as single-select cards
 * (Black-on-Black / White-on-Black, with imagery + copy) plus a full-width "Custom uniform" option.
 * Presentational only: data in via props, selection out via `onValueChange`.
 */
export function UniformSection({ uniform, value, onValueChange, className }: Readonly<UniformSectionProps>) {
  const id = React.useId();
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(next as UniformSectionValue)}
      className={cn("flex flex-col gap-2", className)}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {(uniform?.presets ?? []).map((preset) => (
          <PresetCard
            key={preset.id}
            id={`${id}-${preset.color_scheme}`}
            value={preset.color_scheme}
            title={COLOR_SCHEME_LABELS[preset.color_scheme]}
            description={preset.description}
            image={preset.thumbnail_url || undefined}
            selected={value === preset.color_scheme}
          />
        ))}
      </div>
      {/* Opt-out sits between the presets and Custom: presets (easy) → no uniform → Custom (most effort). */}
      <NotRequiredCard id={`${id}-not-required`} selected={value === "NOT_REQUIRED"} />
      <CustomCard id={`${id}-custom`} selected={value === "CUSTOM"} />
    </RadioGroup>
  );
}

interface PresetCardProps {
  id: string;
  value: ColorSchemeEnum;
  title: string;
  description?: string;
  image?: string;
  selected: boolean;
}

function PresetCard({ id, value, title, description, image, selected }: Readonly<PresetCardProps>) {
  // Keyed by URL so a new, valid thumbnail isn't hidden by a prior 404.
  const [failedImage, setFailedImage] = React.useState<string>();
  return (
    <label htmlFor={id} data-isselected={selected} className={cn(cardClass, "flex flex-col gap-2")}>
      <span className="flex items-center gap-2">
        <RadioGroupItem value={value} id={id} className="shrink-0" />
        <span className="text-normal font-semibold">{title}</span>
      </span>
      {description && <span className="text-small text-gray">{description}</span>}
      {/*
       * One composite `thumbnail_url` per preset, height-capped and centered (no framing box) to keep cards
       * compact. The slot reserves a min height so cards stay balanced in the grid even if a thumbnail 404s.
       */}
      <span className="mt-auto flex min-h-16 w-full items-end justify-center">
        {image && image !== failedImage && (
          <img
            src={image}
            alt={title}
            onError={() => setFailedImage(image)}
            className="max-h-16 w-auto object-contain"
          />
        )}
      </span>
    </label>
  );
}

function CustomCard({ id, selected }: Readonly<{ id: string; selected: boolean }>) {
  return (
    <label
      htmlFor={id}
      aria-label="Custom uniform"
      data-isselected={selected}
      className={cn(cardClass, "flex items-center justify-between gap-3 py-2")}
    >
      <span className="flex flex-col gap-1">
        <span className="flex items-center gap-2">
          <RadioGroupItem value="CUSTOM" id={id} className="shrink-0" />
          <span className="text-normal font-semibold">Custom uniform</span>
        </span>
        <span className="text-small text-gray">Build a uniform from individual garments.</span>
      </span>
      <img src={customUniformImage} alt="" aria-hidden className="size-12 shrink-0 object-contain" />
    </label>
  );
}

function NotRequiredCard({ id, selected }: Readonly<{ id: string; selected: boolean }>) {
  return (
    <label htmlFor={id} data-isselected={selected} className={cn(cardClass, "flex flex-col gap-1")}>
      <span className="flex items-center gap-2">
        <RadioGroupItem value="NOT_REQUIRED" id={id} className="shrink-0" />
        <span className="text-normal font-semibold">No uniform required</span>
      </span>
      <span className="text-small text-gray">This position doesn’t require a specific uniform.</span>
    </label>
  );
}
